import { useEffect, useState } from 'react';
import { useStorage } from '@elfsight/widget-utils';
import { couponSettings } from './couponSettings';

export type CouponMode = 'manual' | 'random' | 'unique';

interface CouponConfig {
  mode?: CouponMode;
  couponCode?: string;
  couponList?: string;
  prefix?: string;
  length?: number;
  characters?: string;
  widgetId: string;
  blockId: string;
  editMode?: boolean;
}

const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

function generateRandomCode(
  prefix: string,
  length: number,
  characters: string
) {
  const getCharset = () => {
    if (characters === 'letters') {
      return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    }
    if (characters === 'numbers') {
      return '0123456789';
    }
    // lettersAndNumbers (default)
    return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  };

  const charset = getCharset();
  let code = '';

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    code += charset[randomIndex];
  }

  return prefix ? `${prefix}${code}` : code;
}

function getCouponMaskRegex(
  prefix: string,
  length: number,
  characters: string
) {
  // Determine character set for regex based on characters param
  const charClass = (() => {
    if (characters === 'letters') {
      return '[A-Z]';
    }
    if (characters === 'numbers') {
      return '[0-9]';
    }
    return '[A-Z0-9]';
  })();

  const safePrefix = prefix
    ? prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    : '';

  return new RegExp(`^${safePrefix}${charClass}{${length}}$`);
}

function getCouponsArray(couponList: string) {
  return couponList
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line);
}

function selectRandomCoupon(couponList: string) {
  const coupons = getCouponsArray(couponList);

  if (coupons.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * coupons.length);
  return coupons[randomIndex];
}

export function useCouponCode(config: CouponConfig) {
  const {
    mode = couponSettings.mode,
    couponCode,
    couponList,
    prefix = couponSettings.prefix,
    length = couponSettings.length,
    characters = couponSettings.characters,
    blockId,
    editMode = false
  } = config;

  const [storedCode, couponStorage] = useStorage<string | null>(
    `coupon_${blockId}`,
    null,
    editMode ? undefined : EXPIRY_TIME
  );

  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    // Manual mode - just use the provided code
    if (mode === 'manual') {
      setCode(couponCode?.trim() || null);
      return;
    }

    // In edit mode, don't use storage - generate code when parameters change
    if (editMode) {
      if (mode === 'random') {
        const randomCoupon = selectRandomCoupon(couponList || '');
        setCode(randomCoupon);
      } else if (mode === 'unique') {
        const uniqueCode = generateRandomCode(prefix, length, characters);
        setCode(uniqueCode);
      } else {
        setCode(null);
      }
      return;
    }

    // Random mode - select random from list
    if (mode === 'random') {
      // Check if we have a stored coupon
      if (storedCode) {
        // Invalidate if storedCode is not in the current couponList
        const coupons = getCouponsArray(couponList || '');
        if (coupons.includes(storedCode)) {
          setCode(storedCode);
        } else {
          couponStorage.remove();
        }
        return;
      }

      // Generate new random coupon
      const randomCoupon = selectRandomCoupon(couponList || '');
      if (randomCoupon) {
        couponStorage.set(randomCoupon);
        setCode(randomCoupon);
      } else {
        setCode(null);
      }
      return;
    }

    // Unique mode - generate unique code
    if (mode === 'unique') {
      // Check if we have a stored coupon
      if (storedCode) {
        // Invalidate if mask does not match
        // Mask = prefix + length + characters
        const maskRegex = getCouponMaskRegex(prefix, length, characters);
        if (maskRegex.test(storedCode)) {
          setCode(storedCode);
        } else {
          couponStorage.remove();
        }
        return;
      }

      // Generate new unique code
      const uniqueCode = generateRandomCode(prefix, length, characters);
      couponStorage.set(uniqueCode);
      setCode(uniqueCode);
      return;
    }

    setCode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    couponCode,
    couponList,
    prefix,
    length,
    characters,
    blockId,
    editMode,
    storedCode
  ]);

  return code;
}
